'use client'

import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import type { ColorBlindFilter, ContrastMode } from '@/lib/a11y/types'

import { useAccessibility } from '../AccessibilityProvider'

const CONTRAST_OPTIONS: { value: ContrastMode; label: string; description: string }[] = [
  { value: 'normal', label: 'Normalno', description: 'Podrazumevane boje' },
  { value: 'high', label: 'Visoki kontrast', description: 'Crna pozadina, zlatni akcenti' },
  { value: 'bw', label: 'Crno-belo', description: 'Bez boja' },
  { value: 'invert', label: 'Invertovano', description: 'Obrnute boje' },
  { value: 'sepia', label: 'Sepia', description: 'Tonovi boje kafe' },
]

const CVD_OPTIONS: { value: ColorBlindFilter; label: string; description: string }[] = [
  { value: 'none', label: 'Bez filtera', description: 'Prikaz bez simulacije daltonizma' },
  {
    value: 'protanopia',
    label: 'Protanopia',
    description: 'Slabije prepoznavanje crvene',
  },
  {
    value: 'deuteranopia',
    label: 'Deuteranopia',
    description: 'Slabije prepoznavanje zelene',
  },
  {
    value: 'tritanopia',
    label: 'Tritanopia',
    description: 'Slabije prepoznavanje plave',
  },
]

export function ColorSection() {
  const { settings, update, announce } = useAccessibility()

  return (
    <section aria-labelledby="a11y-color-heading" className="flex flex-col gap-4">
      <h3 id="a11y-color-heading" className="text-base font-semibold">
        Boja
      </h3>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium">Režim boja</legend>
        <RadioGroup
          value={settings.contrastMode}
          onValueChange={(val) => {
            update({ contrastMode: val as ContrastMode })
            announce(`Režim boja: ${CONTRAST_OPTIONS.find((o) => o.value === val)?.label ?? val}`)
          }}
          className="flex flex-col gap-2"
        >
          {CONTRAST_OPTIONS.map((opt) => (
            <div key={opt.value} className="flex items-start gap-2">
              <RadioGroupItem value={opt.value} id={`contrast-${opt.value}`} className="mt-1" />
              <Label htmlFor={`contrast-${opt.value}`} className="flex flex-col gap-0.5">
                <span>{opt.label}</span>
                <span className="text-muted-foreground text-xs font-normal">{opt.description}</span>
              </Label>
            </div>
          ))}
        </RadioGroup>
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium">Filter za daltonizam</legend>
        <RadioGroup
          value={settings.colorBlindFilter}
          onValueChange={(val) => {
            update({ colorBlindFilter: val as ColorBlindFilter })
            announce(
              `Filter za daltonizam: ${CVD_OPTIONS.find((o) => o.value === val)?.label ?? val}`,
            )
          }}
          className="flex flex-col gap-2"
        >
          {CVD_OPTIONS.map((opt) => (
            <div key={opt.value} className="flex items-start gap-2">
              <RadioGroupItem value={opt.value} id={`cvd-${opt.value}`} className="mt-1" />
              <Label htmlFor={`cvd-${opt.value}`} className="flex flex-col gap-0.5">
                <span>{opt.label}</span>
                <span className="text-muted-foreground text-xs font-normal">{opt.description}</span>
              </Label>
            </div>
          ))}
        </RadioGroup>
      </fieldset>
    </section>
  )
}
